import { SpinnerLoader } from '@assets/icons'
import { Button, ErrorForm, HeaderPage, InputForm } from '@components/index'
import { useYupValidation } from '@hooks/index'
import { ICreateUpdateHighlight } from '@interfaces/index'
import { LocalizationProvider, MobileDateTimePicker, ptBR } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { errorHandler } from '@services/index'
import HighlightService from '@services/requests/highlightService'
import { highlightSchema } from '@yupSchemas/index'
import ptBr from 'dayjs/locale/pt-br'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function HighlightForm(): React.ReactElement {
    const [isLoading, setIsLoading] = React.useState(false)
    const navigate = useNavigate()
    const params = useParams()

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<ICreateUpdateHighlight>({
        resolver: useYupValidation(highlightSchema),
    })

    React.useEffect(() => {
        const getHighLight = async (): Promise<void> => {
            try {
                if (!params.id) return

                const response = await HighlightService.getOne(parseInt(params.id))
                reset({
                    url: response.url,
                    name: response.name,
                    description: response.description,
                    publicationDate: response.publicationDate
                })
            } catch (err) {
                toast(errorHandler(err).message, { type: 'error' })
                navigate('/destaques')
            }
        }
        getHighLight()
    }, [])

    async function submit(values: ICreateUpdateHighlight): Promise<void> {
        setIsLoading(true)

        try {
            if (!params.id) {
                await HighlightService.create(values)
                toast('Destaque salvo com sucesso!', { type: 'success' })
                navigate('/destaques')
                return
            }

            await HighlightService.update(parseInt(params.id), values)
            toast('Destaque salvo com sucesso!', { type: 'success' })
            navigate('/destaques')
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
            reset()
        }
    }

    return (
        <div className="card">
            <div className="highlight-form">
                <HeaderPage title="Editar Destaque" />

                <form onSubmit={handleSubmit(submit)}>
                    <div className="box-form">
                        <div className="box-controller">
                            <Controller
                                name="description"
                                defaultValue={''}
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            placeholder="Descrição"
                                            label="Descrição"
                                            name="description"
                                            value={value}
                                            onChange={onChange}
                                            size='small'
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.description?.message} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="url"
                                control={control}
                                defaultValue={''}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            placeholder="URL do botão"
                                            label="URL do botão"
                                            size='small'
                                            name="url"
                                            value={value}
                                            onChange={onChange}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.url?.message} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="name"
                                defaultValue={''}
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            placeholder="Título do botão"
                                            label="Título do botão"
                                            name="name"
                                            type="text"
                                            value={value}
                                            size='small'
                                            onChange={onChange}
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.name?.message} />
                        </div>

                        <div className="box-controller">
                            <Controller
                                name="publicationDate"
                                control={control}
                                defaultValue={new Date()}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale={ptBr}
                                            localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                                        >
                                            <MobileDateTimePicker
                                                value={value}
                                                onChange={onChange}
                                                inputFormat="DD/MM/YYYY HH:mm"
                                                mask="____/__/__ __:__ _M"
                                                renderInput={(params) => <InputForm size='small' {...params} label={'Data de publicação'} />}
                                                toolbarTitle={'SELECIONE A DATA E A HORA'}
                                            />
                                        </LocalizationProvider>
                                    )
                                }}
                            />
                            <ErrorForm error={errors.publicationDate?.message} />
                        </div>
                    </div>

                    <div className="box-buttons">
                        <Button type="button" secondary placeholder={'Cancelar'} onClick={() => navigate(-1)} />
                        <Button type="submit" placeholder={'Salvar'} />
                    </div>
                </form>
                {isLoading ? (
                    <div className="loader-page">
                        <SpinnerLoader />
                    </div>
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
